import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Building2, Users as UsersIcon, UsersRound } from 'lucide-react'
import Users from './Users'
import Groups from './Groups'
import OpCos from './admin/OpCos'
import { useOpCo } from '../context/OpCoContext'
import { useAuth } from '../context/AuthContext'
import { isAdminOrReader } from '../utils/readerAccess'

const BASE_TABS = [
  { key: 'users', label: 'Users', icon: UsersIcon },
  { key: 'groups', label: 'Groups', icon: UsersRound },
]

const OPCO_TAB = { key: 'opcos', label: 'OpCos', icon: Building2 }

// Users / Groups stay org-admin-only. The OpCos tab is for Super Admins alone:
// an Admin (the manager of their one OpCo) sees no OpCo screens.
export function buildTabs({ enabled, isOpcoAdmin, isOrgAdmin = true } = {}) {
  const opco = enabled && isOpcoAdmin
  if (!isOrgAdmin) return opco ? [OPCO_TAB] : []
  return opco ? [...BASE_TABS, OPCO_TAB] : BASE_TABS
}

export default function UsersGroups() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || null)
  const { enabled, isOpcoAdmin } = useOpCo()
  const { user } = useAuth()
  const isOrgAdmin = isAdminOrReader(user)
  const tabs = useMemo(
    () => buildTabs({ enabled, isOpcoAdmin, isOrgAdmin }),
    [enabled, isOpcoAdmin, isOrgAdmin],
  )
  // A missing or stale ?tab= falls back to the first allowed tab (Users for
  // org admins, OpCos for Super Admins who are not org admins).
  const currentTab = tabs.some((t) => t.key === activeTab) ? activeTab : (tabs[0]?.key || 'users')

  const handleTabChange = (key) => {
    setActiveTab(key)
    setSearchParams({ tab: key })
  }

  return (
    <div className="space-y-5 sm:space-y-6">
      {currentTab === 'users' && (
        <Users
          activeTab={currentTab}
          onTabChange={handleTabChange}
          tabs={tabs}
        />
      )}
      {currentTab === 'groups' && (
        <Groups
          activeTab={currentTab}
          onTabChange={handleTabChange}
          tabs={tabs}
        />
      )}
      {currentTab === 'opcos' && (
        <OpCos
          activeTab={currentTab}
          onTabChange={handleTabChange}
          tabs={tabs}
        />
      )}
    </div>
  )
}
